/**
 * User Store - Pinia store for authentication and user management
 * Most complex store with auth retry queue, pending actions, and quick login
 */

import { defineStore } from 'pinia'
import api from '@/services/api'
import { getAccessToken, getRefreshToken, setAuthTokens, clearAuthTokens } from '@/services/http'
import { setSentryUserId, clearSentryUser } from '@/sentry'
import type {
  UserState,
  UserProfile,
  LoginPayload,
  RegisterPayload,
  UpdateProfilePayload,
  CreateAddressPayload,
  UpdateAddressParams,
  UpdatePreferencesPayload,
  AuthResponse,
  PendingAction,
  ShowQuickLoginPayload
} from './types/user.types'
import type { Address } from './types/common.types'

function extractApiErrorMessage(error: any, fallback = 'An unexpected error occurred.'): string {
  return (
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.error?.message ||
    error?.message ||
    fallback
  )
}

/**
 * Initial state factory function
 */
function createInitialState(): UserState {
  return {
    profile: null,
    email: '',
    showQuickLogin: false,
    quickLoginContext: {
      message: '',
      action: null,
      url: ''
    },
    pendingAuthActions: [],
    preferences: null,
    addresses: [],
    emailVerification: {
      required: false,
      status: 'unverified',
      message: ''
    },
    recentlyViewed: [],
    alerts: [],
    wishlist: [],
    loading: {
      init: false,
      login: false,
      register: false,
      logout: false,
      profile: false,
      avatar: false,
      addresses: false,
      preferences: false,
      verify: false,
      quickAuth: false
    },
    error: {
      login: '',
      register: '',
      profile: '',
      avatar: '',
      addresses: '',
      preferences: '',
      verify: '',
      quickAuth: ''
    }
  }
}

export const useUserStore = defineStore('user', {
  state: (): UserState => createInitialState(),
  getters: {
    /**
     * Check if user is authenticated
     */
    isLoggedIn(state): boolean {
      return state.profile != null
    },

    /**
     * Check if user is authenticated (alias for isLoggedIn)
     */
    isAuthenticated(state): boolean {
      return state.profile != null
    },

    /**
     * Get user profile
     */
    userProfile(state): UserProfile | null {
      return state.profile
    },

    /**
     * Get user addresses
     */
    userAddresses(state): Address[] {
      return state.addresses
    },

    /**
     * Get user preferences
     */
    userPreferences(state) {
      return state.preferences
    },

    /**
     * Get email verification status
     */
    emailVerificationStatus(state) {
      return state.emailVerification
    },

    /**
     * Get loading states
     */
    userLoading(state) {
      return state.loading
    },

    /**
     * Get error states
     */
    userError(state) {
      return state.error
    },

    /**
     * Get user email
     */
    userEmail(state): string {
      return state.email
    },

    /**
     * Get user wishlist
     */
    userWishlist(state): any[] {
      return state.wishlist
    },

    /**
     * Check if product is in wishlist
     */
    isInWishlist(state) {
      return (productId: string): boolean => {
        return state.wishlist.some(item => item.id === productId)
      }
    },

    /**
     * Get quick login dialog state
     */
    quickLoginState(state) {
      return {
        show: state.showQuickLogin,
        context: state.quickLoginContext
      }
    },

    /**
     * Get pending auth actions
     */
    pendingActions(state): PendingAction[] {
      return state.pendingAuthActions
    }
  },

  actions: {
    /**
     * Initialize from storage - read tokens and attempt to fetch profile
     */
    async initFromStorage(): Promise<void> {
      try {
        this.loading.init = true
        const token = getAccessToken()
        const refresh = getRefreshToken()

        if (token || refresh) {
          await this.fetchMe()
          if (this.profile?.id) {
            setSentryUserId(this.profile.id)
          }
        }
        // Clear any stale pending actions on fresh page load
        this.clearPendingActions()
        this.showQuickLogin = false
      } catch (error) {
        // If fetchMe fails (e.g., expired), ensure logged out state
        clearAuthTokens()
        this.clearPendingActions()
        this.showQuickLogin = false
        this.profile = null
      } finally {
        this.loading.init = false
      }
    },

    /**
     * Set user email (for newsletter or guest checkout)
     */
    setEmail(email: string) {
      this.email = email
    },

    /**
     * Login user via API
     */
    async login(payload: LoginPayload): Promise<AuthResponse> {
      this.loading.login = true
      this.error.login = ''

      try {
        const response = await api.auth.login(payload.email, payload.password)
        const accessToken = response?.accessToken
        const refreshToken = response?.refreshToken

        if (accessToken) {
          setAuthTokens({ accessToken, refreshToken })
        }

        // Use user data from login response
        if (response?.user) {
          this.profile = response.user
          setSentryUserId(this.profile?.id)
        }

        // Use preferences from login response if available
        if (response?.preferences) {
          this.preferences = response.preferences
        }

        // Process any pending actions after successful login
        if (this.pendingAuthActions.length > 0) {
          await this.processPendingActions()
        }

        return { success: true, message: 'Login successful!' }
      } catch (error: any) {
        this.profile = null
        this.error.login = extractApiErrorMessage(error, 'Invalid email or password.')
        return { success: false, message: this.error.login }
      } finally {
        this.loading.login = false
      }
    },

    /**
     * Request a one-time code for passwordless login
     */
    async requestOtp(email?: string, phone?: string): Promise<{ success: boolean; message: string }> {
      this.loading.quickAuth = true
      this.error.quickAuth = ''
      if (email) this.email = email

      try {
        const response = await api.auth.requestOtp(email, phone)
        const message = response?.message || 'We sent a code to your WhatsApp number.'
        return { success: true, message }
      } catch (error: any) {
        const message = extractApiErrorMessage(error, 'Failed to send code.')
        this.error.quickAuth = message
        return { success: false, message }
      } finally {
        this.loading.quickAuth = false
      }
    },

    /**
     * Send a magic link for passwordless login
     */
    async sendMagicLink(email?: string, phone?: string): Promise<{ success: boolean; message: string }> {
      this.loading.quickAuth = true
      this.error.quickAuth = ''
      if (email) this.email = email

      try {
        const response = await api.auth.sendMagicLink(email, phone)
        const message = response?.message || 'Magic link sent! Check your email.'
        return { success: true, message }
      } catch (error: any) {
        const message = extractApiErrorMessage(error, 'Failed to send magic link.')
        this.error.quickAuth = message
        return { success: false, message }
      } finally {
        this.loading.quickAuth = false
      }
    },

    /**
     * Verify a one-time code and hydrate the user session
     */
    async verifyOtp(email?: string, otp?: string, phone?: string): Promise<{ success: boolean; message: string }> {
      this.loading.quickAuth = true
      this.error.quickAuth = ''
      if (email) this.email = email

      try {
        const response = await api.auth.verifyOtp(email, otp, phone)
        const accessToken = response?.accessToken
        const refreshToken = response?.refreshToken

        if (accessToken) {
          setAuthTokens({ accessToken, refreshToken })
        }

        if (response?.user) {
          this.profile = response.user
          setSentryUserId(this.profile?.id)
        }

        if (response?.preferences) {
          this.preferences = response.preferences
        }

        // Process any pending actions after successful login
        if (this.pendingAuthActions.length > 0) {
          await this.processPendingActions()
        }

        // Hide quick login dialog state if shown
        this.showQuickLogin = false
        this.quickLoginContext = { message: '', action: null, url: '' }

        return { success: true, message: response?.message || 'Signed in!' }
      } catch (error: any) {
        const message = extractApiErrorMessage(error, 'Invalid code. Please try again.')
        this.error.quickAuth = message
        this.profile = null
        return { success: false, message }
      } finally {
        this.loading.quickAuth = false
      }
    },


    /**
     * Register new user via API
     */
    async register(payload: RegisterPayload): Promise<AuthResponse> {
      this.loading.register = true
      this.error.register = ''

      try {
        const response = await api.auth.register(payload)

        // Some APIs auto-login; others require verification
        const requiresVerification = !!(response?.requiresVerification || response?.emailVerificationRequired)
        this.emailVerification.required = requiresVerification
        this.emailVerification.status = requiresVerification ? 'pending' : 'verified'

        if (response?.accessToken) {
          const accessToken = response.accessToken
          const refreshToken = response.refreshToken
          setAuthTokens({ accessToken, refreshToken })

          // Use user data from register response
          if (response?.user) {
            this.profile = response.user
            setSentryUserId(this.profile?.id)
          }

          // Use preferences from register response if available
          if (response?.preferences) {
            this.preferences = response.preferences
          }
        }

        return { success: true, message: response?.message || 'Registration successful.' }
      } catch (error: any) {
        this.error.register = extractApiErrorMessage(error, 'Registration failed.')
        return { success: false, message: this.error.register }
      } finally {
        this.loading.register = false
      }
    },

    /**
     * Logout user
     */
    async logout(): Promise<void> {
      this.loading.logout = true

      try {
        try {
          await api.auth.logout()
        } catch (error) {
          // Best effort - continue logout even if API call fails
        }
      } finally {
        clearSentryUser()
        clearAuthTokens()
        this.clearPendingActions()

        // Reset state to initial values
        const fresh = createInitialState()
        Object.keys(this.$state).forEach((key) => {
          (this.$state as any)[key] = (fresh as any)[key]
        })

        this.loading.logout = false
      }
    },

    /**
     * Delete the current user's account and clear local session state.
     */
    async deleteAccount(): Promise<AuthResponse> {
      this.loading.profile = true
      this.error.profile = ''

      try {
        await api.user.deleteAccount()
        clearSentryUser()
        clearAuthTokens()
        this.clearPendingActions()

        const fresh = createInitialState()
        Object.keys(this.$state).forEach((key) => {
          (this.$state as any)[key] = (fresh as any)[key]
        })

        return { success: true, message: 'Account deleted successfully.' }
      } catch (error: any) {
        const message = extractApiErrorMessage(error, 'Failed to delete account.')
        this.error.profile = message
        return { success: false, message }
      } finally {
        this.loading.profile = false
      }
    },

    /**
     * Fetch current user profile
     */
    async fetchMe(): Promise<UserProfile | null> {
      this.loading.profile = true
      this.error.profile = ''

      try {
        const me = await api.user.me()
        const profile = (me as any)?.data || (me as any)
        this.profile = profile as UserProfile
        return this.profile
      } catch (error: any) {
        this.error.profile = error?.message || 'Failed to load profile.'
        throw error
      } finally {
        this.loading.profile = false
      }
    },

    /**
     * Update user profile
     */
    async updateProfile(payload: UpdateProfilePayload): Promise<AuthResponse> {
      this.loading.profile = true
      this.error.profile = ''

      try {
        const updated = await api.user.update(payload)
        const updatedProfile = (updated as any)?.data || (updated as any) || payload
        this.profile = { ...(this.profile || ({} as UserProfile)), ...(updatedProfile as any) }
        return { success: true, message: 'Profile updated.' }
      } catch (error: any) {
        this.error.profile = error?.message || 'Profile update failed.'
        return { success: false, message: this.error.profile }
      } finally {
        this.loading.profile = false
      }
    },

    /**
     * Upload user avatar
     */
    async uploadAvatar(file: File): Promise<AuthResponse> {
      this.loading.avatar = true
      this.error.avatar = ''

      try {
        const response = await api.user.uploadAvatar(file)

        // Expect updated avatar URL from server
        const avatarUrl =
          (response as any)?.data?.avatarUrl ||
          (response as any)?.avatarUrl ||
          (response as any)?.avatar ||
          (response as any)?.url

        if (avatarUrl) {
          this.profile = {
            ...(this.profile || ({} as UserProfile)),
            avatar: avatarUrl,
            avatarUrl
          }
        } else {
          // Fallback: refetch profile
          await this.fetchMe()
        }

        return { success: true, message: 'Avatar uploaded successfully.' }
      } catch (error: any) {
        this.error.avatar = error?.message || 'Avatar upload failed.'
        return { success: false, message: this.error.avatar }
      } finally {
        this.loading.avatar = false
      }
    },

    /**
     * List user addresses
     */
    async listAddresses(): Promise<Address[]> {
      this.loading.addresses = true
      this.error.addresses = ''

      try {
        const list = await api.user.addresses.list()
        const items =
          Array.isArray(list) ? list : ((list as any)?.data || (list as any)?.items || [])
        this.addresses = items as Address[]
        return this.addresses
      } catch (error: any) {
        this.error.addresses = error?.message || 'Failed to load addresses.'
        return []
      } finally {
        this.loading.addresses = false
      }
    },

    /**
     * Create new address
     */
    async createAddress(payload: CreateAddressPayload): Promise<Address> {
      this.loading.addresses = true

      try {
        const createdResponse = await api.user.addresses.create(payload as any)
        const created = (createdResponse as any)?.data || (createdResponse as any)
        await this.listAddresses()
        return created as Address
      } finally {
        this.loading.addresses = false
      }
    },

    /**
     * Update existing address
     */
    async updateAddress(params: UpdateAddressParams): Promise<Address> {
      this.loading.addresses = true

      try {
        const updatedResponse = await api.user.addresses.update(params.id, params.payload as any)
        const updated = (updatedResponse as any)?.data || (updatedResponse as any)
        await this.listAddresses()
        return updated as Address
      } finally {
        this.loading.addresses = false
      }
    },

    /**
     * Remove address
     */
    async removeAddress(id: string): Promise<AuthResponse> {
      this.loading.addresses = true

      try {
        await api.user.addresses.remove(id)
        await this.listAddresses()
        return { success: true, message: 'Address removed.' }
      } finally {
        this.loading.addresses = false
      }
    },

    /**
     * Set primary address
     */
    async setPrimaryAddress(id: string): Promise<AuthResponse> {
      this.loading.addresses = true

      try {
        await api.user.addresses.setPrimary(id)
        await this.listAddresses()
        return { success: true, message: 'Primary address set.' }
      } finally {
        this.loading.addresses = false
      }
    },

    /**
     * Get user preferences
     */
    async getPreferences(): Promise<any> {
      this.loading.preferences = true
      this.error.preferences = ''

      try {
        const prefs = await api.user.preferences.get()
        const prefsData = (prefs as any)?.data || (prefs as any)
        this.preferences = prefsData as any
        return prefsData
      } catch (error: any) {
        this.error.preferences = error?.message || 'Failed to load preferences.'
        return null
      } finally {
        this.loading.preferences = false
      }
    },

    /**
     * Update user preferences
     */
    async updatePreferences(payload: UpdatePreferencesPayload): Promise<any> {
      this.loading.preferences = true

      try {
        const response = await api.user.preferences.update(payload)
        const prefsData = (response as any)?.data || (response as any)
        this.preferences = { ...(this.preferences || {}), ...(prefsData || payload) }
        return prefsData
      } finally {
        this.loading.preferences = false
      }
    },

    /**
     * Verify email with token
     */
    async verifyEmail(token: string): Promise<AuthResponse> {
      this.loading.verify = true
      this.error.verify = ''

      try {
        const response = await api.auth.verifyEmail(token)
        this.emailVerification.status = 'verified'
        this.emailVerification.required = false

        // Refresh profile
        try {
          await this.fetchMe()
        } catch (error) {
          // Ignore fetch error
        }

        return { success: true, message: response?.message || 'Email verified.' }
      } catch (error: any) {
        this.error.verify = error?.message || 'Verification failed.'
        this.emailVerification.status = 'unverified'
        return { success: false, message: this.error.verify }
      } finally {
        this.loading.verify = false
      }
    },

    /**
     * Add product to local wishlist (fallback)
     */
    addToWishlist(product: any) {
      if (!this.wishlist.some(item => item.id === product.id)) {
        this.wishlist.push(product)
        console.log(`Added ${product.name} to wishlist`)
      }
    },

    /**
     * Remove product from local wishlist (fallback)
     */
    removeFromWishlist(productId: string) {
      const index = this.wishlist.findIndex(item => item.id === productId)
      if (index > -1) {
        this.wishlist.splice(index, 1)
        console.log('Removed product from wishlist')
      }
    },

    /**
     * Clear local wishlist
     */
    clearWishlist() {
      this.wishlist = []
      console.log('Wishlist cleared')
    },

    /**
     * Show quick login dialog
     */
    showQuickLoginDialog(payload: ShowQuickLoginPayload) {
      this.quickLoginContext = {
        message: payload.message || 'Please sign in to continue',
        action: payload.action,
        url: payload.url
      }
      this.showQuickLogin = true
    },

    /**
     * Hide quick login dialog
     */
    hideQuickLoginDialog() {
      this.showQuickLogin = false

      // Clear context after a delay to avoid flickering
      setTimeout(() => {
        this.quickLoginContext = {
          message: '',
          action: null,
          url: ''
        }
      }, 300)
    },

    /**
     * Queue an action to be retried after login
     */
    queueAuthAction(actionData: Omit<PendingAction, 'timestamp'>) {
      this.pendingAuthActions.push({
        ...actionData,
        timestamp: Date.now()
      })
    },

    /**
     * Clear all pending actions
     */
    clearPendingActions() {
      this.pendingAuthActions = []
    },

    /**
     * Process all pending actions after successful login
     */
    async processPendingActions(): Promise<void> {
      const actions = [...this.pendingAuthActions]
      this.pendingAuthActions = []

      for (const action of actions) {
        try {
          // Re-execute the original action
          if (action.type === 'apiCall') {
            const http = (await import('@/services/http')).default
            const config = {
              method: action.method || 'GET',
              url: action.url,
              data: action.data,
              params: action.params
            }
            await http(config)
          } else if (action.type === 'custom' && action.handler) {
            await action.handler()
          }
        } catch (error) {
          console.error('Failed to retry action after login:', error)

          // Show error notification via UI store
          const { useUIStore } = await import('./useUIStore')
          const uiStore = useUIStore()
          uiStore.showError('Failed to complete action. Please try again.')
        }
      }

      if (actions.length > 0) {
        // Show success notification via UI store
        const { useUIStore } = await import('./useUIStore')
        const uiStore = useUIStore()
        uiStore.showSuccess(`Successfully signed in! Completed ${actions.length} action(s).`)
      }
    }
  },
  persist: true
})
